import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaidIcon from '@mui/icons-material/Paid';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/hooks';
import { RootState } from '../../redux/reducers';
import { importArticles } from '../../redux/actions/articleActions';
import { deleteProductTemplate, getAllProductTemplates, ProductTemplateModel } from '../../api/Extensions';
import ProductTemplateForm from './ProductTemplateForm';
import ProductTemplateCostView from './ProductTemplateCostView';

export default function ProductTemplatesList(): ReactElement {
    const dispatch = useAppDispatch();
    const articles = useSelector((state: RootState) => state.articles.articles);

    const [templates, setTemplates] = useState<ProductTemplateModel[] | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<ProductTemplateModel | undefined>(undefined);
    const [costOpen, setCostOpen] = useState(false);
    const [priced, setPriced] = useState<ProductTemplateModel | undefined>(undefined);

    const refresh = useCallback(async () => {
        const data = await getAllProductTemplates();
        setTemplates(data);
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!articles) {
            dispatch(importArticles());
        }
    }, [dispatch, articles]);

    const openNew = () => {
        setEditing(undefined);
        setFormOpen(true);
    };

    const openEdit = (template: ProductTemplateModel) => {
        setEditing(template);
        setFormOpen(true);
    };

    // Clone: seed the form from an existing template but drop the id, so saving creates a new one.
    const openClone = (template: ProductTemplateModel) => {
        setEditing({
            title: 'Kopija - ' + template.title,
            description: template.description,
            items: template.items?.map((i) => ({ articleId: i.articleId, quantity: i.quantity })),
        });
        setFormOpen(true);
    };

    const openCost = (template: ProductTemplateModel) => {
        setPriced(template);
        setCostOpen(true);
    };

    const handleDelete = async (template: ProductTemplateModel) => {
        if (!template.id || !window.confirm(`Obrisati šablon "${template.title}"?`)) {
            return;
        }
        await deleteProductTemplate(template.id);
        await refresh();
    };

    const handleSaved = async () => {
        setFormOpen(false);
        await refresh();
    };

    if (templates === null) {
        return (
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    return (
        <Box sx={{ p: 4, textAlign: 'start' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Šabloni (sastavnice)</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
                    Novi šablon
                </Button>
            </Box>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Naslov</TableCell>
                            <TableCell>Opis</TableCell>
                            <TableCell align="right">Br. artikala</TableCell>
                            <TableCell align="right">Akcije</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {templates.map((t) => (
                            <TableRow key={t.id} hover>
                                <TableCell>{t.title}</TableCell>
                                <TableCell>{t.description}</TableCell>
                                <TableCell align="right">{t.items?.length ?? 0}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Vidi cenu">
                                        <IconButton color="primary" onClick={() => openCost(t)}>
                                            <PaidIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Izmeni šablon">
                                        <IconButton onClick={() => openEdit(t)}>
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Kopiraj u novi šablon">
                                        <IconButton onClick={() => openClone(t)}>
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Obriši">
                                        <IconButton onClick={() => handleDelete(t)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {templates.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    Nema šablona. Kreiraj prvi.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ProductTemplateForm
                open={formOpen}
                template={editing}
                articles={articles ?? []}
                onClose={() => setFormOpen(false)}
                onSaved={handleSaved}
            />

            <ProductTemplateCostView
                open={costOpen}
                template={priced}
                articles={articles ?? []}
                onClose={() => setCostOpen(false)}
            />
        </Box>
    );
}
